import {
  AutoMode,
  Build,
  CalendarMonth,
  CheckCircleOutline,
  Delete,
  Edit,
  Email,
  Event,
  HandshakeOutlined,
  MarkEmailRead,
  OpenInNew,
  Phone,
  Replay,
} from '@mui/icons-material';
import {
  alpha,
  Avatar,
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import {
  Task,
  TASK_EVENT_LABELS,
  TaskAutomationLevel,
  TaskEvent,
} from '@otw/models/tasks';
import { useContacts } from 'hooks/contacts/useContacts';
import React, { useMemo } from 'react';
import { getContactForTask } from '../../util/contactTaskActivity';

interface AutomationDisplay {
  label: string;
  tooltip: string;
  icon: React.ReactNode;
  color: 'success' | 'primary' | 'warning';
}

const AUTOMATION_DISPLAY: Record<TaskAutomationLevel, AutomationDisplay> = {
  full: {
    label: 'Auto',
    tooltip: 'Full automation — auto-completes at 9 AM on the due date',
    icon: <AutoMode fontSize="inherit" />,
    color: 'success',
  },
  partial: {
    label: 'Partial',
    tooltip: 'You\'ll send the message yourself when completing this task',
    icon: <HandshakeOutlined fontSize="inherit" />,
    color: 'primary',
  },
  manual: {
    label: 'Manual',
    tooltip: 'Completed without sending a message',
    icon: <Build fontSize="inherit" />,
    color: 'warning',
  },
};

interface TaskCardProps {
  task: Task;
  onToggleComplete: (task: Task) => void;
  onEdit?: (task: Task) => void;
  onDelete?: (task: Task) => void;
  /** Opens the dialog showing the email/text sent on completion. */
  onViewCommunication?: (task: Task) => void;
  /** Opens the full-page contact detail view for this task's contact. */
  onOpenContact?: (task: Task) => void;
  compact?: boolean;
}

const getInitials = (name: string): string => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const TaskCard: React.FC<TaskCardProps> = ({
  task,
  onToggleComplete,
  onEdit,
  onDelete,
  onViewCommunication,
  onOpenContact,
  compact = false,
}) => {
  const theme = useTheme();
  const { contacts } = useContacts();
  const contact = useMemo(() => getContactForTask(contacts, task), [contacts, task]);
  const displayName = contact?.name || 'Unknown contact';
  const contactEmail = contact?.email ?? null;
  const contactPhone = contact?.phone ?? null;

  const hasSentCommunication = Boolean(task.completionEmail || task.completionText);
  const showViewCommunication =
    Boolean(onViewCommunication) && task.status === 'completed' && hasSentCommunication;
  const showOpenContact = Boolean(onOpenContact) && Boolean(task.contact_id);

  const accentByEvent: Record<TaskEvent, string> = {
    birthday: theme.palette.secondary.main,
    truck_anniversary: theme.palette.success.main,
    lead_lost: theme.palette.error.main,
    check_in: theme.palette.info.main,
    follow_up: theme.palette.primary.main,
    custom: theme.palette.warning.main,
  };
  const accent = accentByEvent[task.event] ?? theme.palette.warning.main;
  const isCompleted = task.status === 'completed';
  const due = dayjs(task.dueDate);
  const isOverdue = !isCompleted && due.isBefore(dayjs(), 'day');
  const dueLabel = due.isValid() ? due.format('ddd, MMM D, YYYY') : 'No due date';
  const automationLevel: TaskAutomationLevel = task.automationLevel ?? 'partial';
  const automation = AUTOMATION_DISPLAY[automationLevel];

  if (compact) {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 1.25,
          borderRadius: 2,
          borderLeft: `4px solid ${accent}`,
          background: isCompleted
            ? alpha(theme.palette.success.main, 0.1)
            : isOverdue
              ? alpha(theme.palette.error.main, 0.1)
              : 'inherit',
          opacity: isCompleted ? 0.85 : 1,
        }}
      >
        <Stack spacing={1} sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0 }}>
            <Avatar
              sx={{
                bgcolor: alpha(accent, 0.15),
                color: accent,
                fontWeight: 700,
                width: 28,
                height: 28,
                fontSize: 12,
              }}
            >
              {getInitials(displayName)}
            </Avatar>
            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Typography
                variant="body2"
                fontWeight={700}
                sx={{
                  textDecoration: isCompleted ? 'line-through' : 'none',
                  lineHeight: 1.2,
                }}
                noWrap
              >
                {task.title}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                {displayName}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            <Chip
              size="small"
              icon={<Event fontSize="inherit" />}
              label={TASK_EVENT_LABELS[task.event]}
              sx={{
                fontWeight: 600,
                bgcolor: alpha(accent, 0.12),
                color: accent,
                border: `1px solid ${alpha(accent, 0.3)}`,
              }}
            />
            <Tooltip title={automation.tooltip}>
              <Chip
                size="small"
                icon={automation.icon as React.ReactElement}
                label={automation.label}
                color={automation.color}
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
            </Tooltip>
          </Stack>

          {task.notes && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                whiteSpace: 'pre-wrap',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {task.notes}
            </Typography>
          )}

          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
            {showViewCommunication && (
              <Tooltip title="View sent email/text">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => onViewCommunication?.(task)}
                >
                  <MarkEmailRead fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={isCompleted ? 'Mark as pending' : 'Mark as complete'}>
              <IconButton
                size="small"
                onClick={() => onToggleComplete(task)}
                color={isCompleted ? 'default' : 'success'}
              >
                {isCompleted ? <Replay fontSize="small" /> : <CheckCircleOutline fontSize="small" />}
              </IconButton>
            </Tooltip>
            {onEdit && (
              <Tooltip title="Edit task">
                <IconButton size="small" onClick={() => onEdit(task)}>
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip title="Delete task">
                <IconButton size="small" color="error" onClick={() => onDelete(task)}>
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        borderLeft: `4px solid ${accent}`,
        background: isCompleted
          ? alpha(theme.palette.success.main, 0.1)
          : isOverdue
            ? alpha(theme.palette.error.main, 0.1)
            : 'inherit',
        opacity: isCompleted ? 0.85 : 1,
        transition: 'background-color 150ms ease',
      }}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexGrow: 1, minWidth: 0 }}>
          <Avatar
            sx={{
              bgcolor: alpha(accent, 0.15),
              color: accent,
              fontWeight: 700,
            }}
          >
            {getInitials(displayName)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              fontWeight={700}
              sx={{
                textDecoration: isCompleted ? 'line-through' : 'none',
                lineHeight: 1.2,
              }}
              noWrap
            >
              {task.title}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="body2" color="text.secondary" noWrap>
                {displayName}
              </Typography>
              {contactEmail && (
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.secondary' }}>
                  <Email fontSize="inherit" />
                  <Typography variant="caption" noWrap>
                    {contactEmail}
                  </Typography>
                </Stack>
              )}
              {contactPhone && (
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.secondary' }}>
                  <Phone fontSize="inherit" />
                  <Typography variant="caption" noWrap>
                    {contactPhone}
                  </Typography>
                </Stack>
              )}
            </Stack>
            {task.notes && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}
              >
                {task.notes}
              </Typography>
            )}
          </Box>
        </Stack>

        <Stack
          direction={{ xs: 'row', md: 'column' }}
          spacing={1}
          alignItems={{ md: 'flex-end' }}
          sx={{ minWidth: 180 }}
        >
          <Chip
            size="small"
            icon={<Event fontSize="inherit" />}
            label={TASK_EVENT_LABELS[task.event]}
            sx={{
              fontWeight: 600,
              bgcolor: alpha(accent, 0.12),
              color: accent,
              border: `1px solid ${alpha(accent, 0.3)}`,
            }}
          />
          <Chip
            size="small"
            icon={<CalendarMonth fontSize="inherit" />}
            label={dueLabel}
            color={isCompleted ? 'success' : isOverdue ? 'error' : 'default'}
            variant={isCompleted || isOverdue ? 'filled' : 'outlined'}
            sx={{ fontWeight: 600 }}
          />
          <Tooltip title={automation.tooltip}>
            <Chip
              size="small"
              icon={automation.icon as React.ReactElement}
              label={automation.label}
              color={automation.color}
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
          </Tooltip>
        </Stack>

        <Stack direction="row" spacing={0.5}>
          {showOpenContact && (
            <Tooltip title="Open contact">
              <IconButton
                size="small"
                onClick={() => onOpenContact?.(task)}
                aria-label="Open contact"
              >
                <OpenInNew fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {showViewCommunication && (
            <Tooltip title="View sent email/text">
              <IconButton
                size="small"
                color="primary"
                onClick={() => onViewCommunication?.(task)}
              >
                <MarkEmailRead fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={isCompleted ? 'Mark as pending' : 'Mark as complete'}>
            <IconButton
              size="small"
              onClick={() => onToggleComplete(task)}
              color={isCompleted ? 'default' : 'success'}
            >
              {isCompleted ? <Replay fontSize="small" /> : <CheckCircleOutline fontSize="small" />}
            </IconButton>
          </Tooltip>
          {onEdit && (
            <Tooltip title="Edit task">
              <IconButton size="small" onClick={() => onEdit(task)}>
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {onDelete && (
            <Tooltip title="Delete task">
              <IconButton size="small" color="error" onClick={() => onDelete(task)}>
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
};

export default TaskCard;
